import React, { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  KeyRound, Plus, Trash2, Copy, Check, ShieldCheck, Webhook, Send,
  Radio, Terminal, Eye, AlertTriangle, RefreshCw, Plug,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(text); setDone(true); setTimeout(() => setDone(false), 1400); }}
      className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-700 hover:text-cyan-900"
      data-testid={testid}
    >
      {done ? <Check size={13} /> : <Copy size={13} />} {done ? "Copied" : "Copy"}
    </button>
  );
}

const ENDPOINTS = [
  ["GET",  "/api/ext/summary", "Live monitoring snapshot — client counts by status, work-queue totals, next meeting, recent activity"],
  ["GET",  "/api/ext/events?since=<iso>", "Poll for changes since a timestamp (activity feed)"],
  ["GET",  "/api/ext/ping", "Verify your key & see which account it maps to"],
  ["GET",  "/api/admin/clients", "Full client list (read)"],
  ["PUT",  "/api/admin/clients/{id}", "Update a client — status, next steps, notes (write)"],
  ["GET",  "/api/admin/work-queue?kind=credit_report", "Work-queue board (read)"],
  ["PATCH","/api/admin/work-queue/{id}", "Move / prioritize / assign a queue card (write)"],
  ["GET",  "/api/meetings", "Meetings & bookings (read)"],
];

export default function AdminIntegrations() {
  const [overview, setOverview] = useState(null);
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [keyName, setKeyName] = useState("");
  const [hookUrl, setHookUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null); // one-time plaintext reveal
  const [copiedKey, setCopiedKey] = useState(false);

  const load = useCallback(async () => {
    try {
      const [o, k, w] = await Promise.all([
        api.get("/admin/integration/overview"),
        api.get("/admin/integration/keys"),
        api.get("/admin/integration/webhooks"),
      ]);
      setOverview(o.data);
      setKeys(k.data?.items || []);
      setHooks(w.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createKey = async () => {
    if (!keyName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/integration/keys", { name: keyName.trim() });
      setNewKey(data);
      setKeyName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create key");
    } finally { setBusy(false); }
  };

  const revokeKey = async (pub) => {
    if (!window.confirm("Revoke this key? Any app using it will immediately lose access.")) return;
    try {
      await api.post(`/admin/integration/keys/${pub}/revoke`);
      toast.success("Key revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Revoke failed");
    }
  };

  const addHook = async () => {
    if (!hookUrl.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/integration/webhooks", { url: hookUrl.trim(), events: ["all"], active: true });
      toast.success("Webhook added");
      setHookUrl("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add webhook");
    } finally { setBusy(false); }
  };

  const testHook = async (id) => {
    try {
      const { data } = await api.post(`/admin/integration/webhooks/${id}/test`);
      if (data.ok) toast.success(`Test delivered (HTTP ${data.status})`);
      else toast.error(`Test failed: ${data.error || data.status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Test failed");
    }
  };

  const deleteHook = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await api.delete(`/admin/integration/webhooks/${id}`);
      toast.success("Webhook deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="admin-integrations">
        {/* Header */}
        <div className="flex items-start gap-3 mb-6">
          <div className="h-11 w-11 rounded-xl bg-slate-900 grid place-items-center shrink-0">
            <Plug className="text-cyan-300" size={22} />
          </div>
          <div>
            <h1 className="font-display text-3xl font-black text-slate-900 leading-tight">API &amp; Integrations</h1>
            <p className="text-slate-500 text-sm mt-1">Connect your other Emergent app — full read/write control, live monitoring, and event webhooks. Keys are tied to your account.</p>
          </div>
        </div>

        {/* Overview */}
        {overview && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Active keys", value: overview.active_keys, icon: KeyRound },
              { label: "Total keys", value: overview.total_keys, icon: ShieldCheck },
              { label: "Webhooks", value: overview.webhooks, icon: Webhook },
              { label: "Active webhooks", value: overview.active_webhooks, icon: Radio },
            ].map((s) => (
              <Card key={s.label} className="p-4 rounded-2xl border-slate-100">
                <s.icon size={16} className="text-slate-400" />
                <p className="font-display text-2xl font-black text-slate-900 mt-2">{s.value}</p>
                <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mt-0.5">{s.label}</p>
              </Card>
            ))}
          </div>
        )}

        {/* One-time key reveal */}
        {newKey && (
          <Card className="p-5 rounded-2xl border-2 border-amber-300 bg-amber-50 mb-6" data-testid="new-key-reveal">
            <div className="flex items-center gap-2 text-amber-800 font-black"><AlertTriangle size={16} /> Copy this key now — it won't be shown again</div>
            <p className="text-sm text-amber-700 mt-1">Store it as a secret in your other app. Send it on every request as the <code className="px-1 rounded bg-amber-100">X-API-Key</code> header.</p>
            <div className="mt-3 flex items-center gap-2 rounded-xl bg-slate-900 text-cyan-200 font-mono text-sm px-4 py-3 break-all">
              <span className="flex-1" data-testid="new-key-value">{newKey.api_key}</span>
              <button
                onClick={() => { navigator.clipboard?.writeText(newKey.api_key); setCopiedKey(true); setTimeout(() => setCopiedKey(false), 1500); }}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-bold bg-cyan-400 text-slate-950 rounded-lg px-3 py-1.5"
                data-testid="copy-new-key"
              >
                {copiedKey ? <Check size={13} /> : <Copy size={13} />} {copiedKey ? "Copied" : "Copy"}
              </button>
            </div>
            <Button onClick={() => setNewKey(null)} className="mt-3 bg-slate-900 hover:bg-slate-800" data-testid="dismiss-new-key">I've saved it</Button>
          </Card>
        )}

        {/* API Keys */}
        <Card className="p-5 rounded-2xl border-slate-100 mb-6">
          <div className="flex items-center gap-2 mb-1"><KeyRound size={18} className="text-slate-700" /><h2 className="font-display text-xl font-black text-slate-900">API Keys</h2></div>
          <p className="text-sm text-slate-500 mb-4">One key per external app. Revoke instantly if a key is ever exposed.</p>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="flex-1">
              <Label className="text-xs">Name this key</Label>
              <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="e.g. My monitoring app" data-testid="key-name-input" />
            </div>
            <Button onClick={createKey} disabled={busy || !keyName.trim()} className="bg-cyan-600 hover:bg-cyan-700 self-end" data-testid="create-key-btn">
              <Plus size={15} className="mr-1" /> Generate key
            </Button>
          </div>
          <div className="space-y-2">
            {keys.length === 0 && <p className="text-sm text-slate-400 py-2">No keys yet.</p>}
            {keys.map((k) => (
              <div key={k.id} className={`flex items-center gap-3 rounded-xl border p-3 ${k.revoked_at ? "border-slate-100 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`} data-testid={`key-row-${k.public_id}`}>
                <KeyRound size={16} className={k.revoked_at ? "text-slate-300" : "text-cyan-600"} />
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-900 text-sm truncate">{k.name} {k.revoked_at && <span className="text-rose-500 text-xs">· revoked</span>}</p>
                  <p className="font-mono text-xs text-slate-400">{k.masked}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Last used: {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}</p>
                </div>
                {!k.revoked_at && (
                  <Button size="sm" variant="ghost" onClick={() => revokeKey(k.public_id)} className="text-rose-600 hover:bg-rose-50" data-testid={`revoke-key-${k.public_id}`}>
                    <Trash2 size={14} className="mr-1" /> Revoke
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Webhooks */}
        <Card className="p-5 rounded-2xl border-slate-100 mb-6">
          <div className="flex items-center gap-2 mb-1"><Webhook size={18} className="text-slate-700" /><h2 className="font-display text-xl font-black text-slate-900">Webhooks</h2></div>
          <p className="text-sm text-slate-500 mb-4">We POST every activity event to your URL, signed with HMAC-SHA256 (headers <code className="px-1 rounded bg-slate-100">X-Webhook-Signature</code>, <code className="px-1 rounded bg-slate-100">X-Webhook-Timestamp</code>, <code className="px-1 rounded bg-slate-100">X-Webhook-Id</code>). Verify with the secret below.</p>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="flex-1">
              <Label className="text-xs">Your receiver URL</Label>
              <Input value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://your-app.com/webhooks/creditquack" data-testid="hook-url-input" />
            </div>
            <Button onClick={addHook} disabled={busy || !hookUrl.trim()} className="bg-slate-900 hover:bg-slate-800 self-end" data-testid="add-hook-btn">
              <Plus size={15} className="mr-1" /> Add webhook
            </Button>
          </div>
          <div className="space-y-2">
            {hooks.length === 0 && <p className="text-sm text-slate-400 py-2">No webhooks yet. Add one when your other app has a receiver ready.</p>}
            {hooks.map((w) => (
              <div key={w.id} className="rounded-xl border border-slate-200 p-3" data-testid={`hook-row-${w.id}`}>
                <div className="flex items-center gap-3">
                  <Webhook size={16} className="text-slate-500" />
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs text-slate-800 truncate">{w.url}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      {w.delivery_count || 0} deliveries · last: {w.last_status ? `HTTP ${w.last_status}` : "—"}
                      {w.last_delivery_at ? ` · ${new Date(w.last_delivery_at).toLocaleTimeString()}` : ""}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => testHook(w.id)} className="text-cyan-700 hover:bg-cyan-50" data-testid={`test-hook-${w.id}`}><Send size={13} className="mr-1" /> Test</Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteHook(w.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`delete-hook-${w.id}`}><Trash2 size={14} /></Button>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                  <span className="text-slate-400 font-semibold">Signing secret:</span>
                  <span className="font-mono text-slate-700 truncate flex-1">{w.secret}</span>
                  <CopyBtn text={w.secret} testid={`copy-secret-${w.id}`} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* How to connect */}
        <Card className="p-5 rounded-2xl border-slate-100 bg-slate-900 text-slate-100">
          <div className="flex items-center gap-2 mb-3"><Terminal size={18} className="text-cyan-300" /><h2 className="font-display text-xl font-black">How to connect</h2></div>
          <div className="flex items-center gap-2 text-sm mb-2">
            <span className="text-slate-400">Base URL:</span>
            <span className="font-mono text-cyan-200 truncate">{API_BASE}</span>
            <CopyBtn text={API_BASE} testid="copy-base-url" />
          </div>
          <pre className="bg-black/40 rounded-xl p-3 text-xs text-cyan-100 overflow-x-auto font-mono mb-4" data-testid="curl-example">{`curl "${API_BASE}/ext/summary" \\
  -H "X-API-Key: cq_live_..."`}</pre>
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2">Handy endpoints (all accept your X-API-Key)</p>
          <div className="space-y-1.5">
            {ENDPOINTS.map(([m, path, desc]) => (
              <div key={path} className="flex items-start gap-2 text-xs">
                <span className={`font-mono font-bold px-1.5 py-0.5 rounded shrink-0 ${m === "GET" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{m}</span>
                <span className="font-mono text-slate-200 shrink-0">{path}</span>
                <span className="text-slate-400">— {desc}</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-4 flex items-center gap-1"><Eye size={12} /> This key acts as your account, so it can do anything you can in the admin. Keep it secret.</p>
        </Card>
      </div>
    </AppShell>
  );
}
